import { useMutation, useQueryClient } from '@tanstack/react-query'
import { bulkUpdateUserEventStatuses } from 'app/frontend/base/api/event'
import { awaitPendingJob, displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type BulkUpdateStatusInput = {
  eventIds: number[]
  nextStatus: wowaudit.EventParticipantReference['status']
}

type BulkStatusJobContent = {
  updatedEventIds: number[]
  errors: { event_id?: number; error: string }[]
}

const useBulkUserEventStatusMutation = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { mutate: bulkUpdateStatus, isPending: isBulkUpdating } = useMutation({
    mutationFn: async ({ eventIds, nextStatus }: BulkUpdateStatusInput) => {
      const response = await bulkUpdateUserEventStatuses(eventIds, nextStatus)
      const jobResult = await new Promise<
        wowaudit.JobStatus<BulkStatusJobContent>
      >((resolve) => {
        awaitPendingJob<BulkStatusJobContent>(response.job_id, resolve)
      })
      if (jobResult.error) {
        throw new Error(jobResult.error)
      }
      return jobResult.content
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['teamEvents'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['userEvents'],
        }),
      ])
      toast.success(t('dashboard.team.events.status-updated'))
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  return {
    bulkUpdateStatus,
    isBulkUpdating,
  }
}

export default useBulkUserEventStatusMutation
